/**
 * Team Form Modal Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Modal dialog for creating and editing teams.
 */

import React, { useState, useEffect } from 'react';
import { X, Users } from 'lucide-react';
import type { Team, TeamCreateData, TeamUpdateData, UserListItem } from '../../../types/admin';
import { userManagementService } from '../../../services/admin';
import { Button } from '../../ui/button';

interface TeamFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TeamCreateData | TeamUpdateData) => Promise<void>;
  team?: Team | null;
  isLoading?: boolean;
}

const TeamFormModal: React.FC<TeamFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  team,
  isLoading = false,
}) => {
  const isEdit = !!team;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    lead_user_id: '',
  });
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load users for lead selection
  useEffect(() => {
    if (isOpen) {
      setLoadingUsers(true);
      userManagementService
        .listUsers({ is_active: true, page_size: 100 })
        .then((response) => {
          setUsers(response.users);
        })
        .catch((err) => {
          console.error('Failed to load users:', err);
        })
        .finally(() => {
          setLoadingUsers(false);
        });
    }
  }, [isOpen]);

  // Reset form when modal opens/closes or team changes
  useEffect(() => {
    if (isOpen) {
      if (team) {
        setFormData({
          name: team.name,
          description: team.description || '',
          lead_user_id: team.lead_user_id || '',
        });
      } else {
        setFormData({
          name: '',
          description: '',
          lead_user_id: '',
        });
      }
      setErrors({});
    }
  }, [isOpen, team]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Team name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Team name must be less than 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    await onSubmit({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      lead_user_id: formData.lead_user_id || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Users size={20} className="text-[#002B45]" />
              <h2 className="text-lg font-semibold text-gray-900">
                {isEdit ? 'Edit Team' : 'Create Team'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    ${errors.name ? 'border-red-300' : 'border-gray-300'}
                  `}
                  placeholder="e.g., Wealth Management Team"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent resize-none"
                  placeholder="Describe the team's purpose..."
                />
              </div>

              {/* Team Lead */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Lead
                </label>
                <select
                  value={formData.lead_user_id}
                  onChange={(e) => setFormData({ ...formData, lead_user_id: e.target.value })}
                  disabled={loadingUsers}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent disabled:bg-gray-50"
                >
                  <option value="">No lead assigned</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name || user.email}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  The team lead will be automatically added as a member
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Team'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TeamFormModal;
